/* eslint-disable no-unused-vars */
/* eslint-disable no-undef */

import { PPI, BLEED, MATERIAL_THICKNESS, MATERIAL_THICKNESS_SHIPPING, COLOR_KRAFT, COLOR_WHITE } from '@/box/constants';
export const useConfigurationStore = defineStore("configuration", () => {
  // STATE ------------------------------------------------------------------
  const product = ref(null);
  const pixelsPerInch = ref(PPI);
  const fabricCanvases = ref([]);
  const activeCanvas = ref(null);
  const offscreenCanvasIds = ref([]);
  const box = ref({});
  const configurationScreenshotData = ref(null);
  const hoveredPanel = ref(null);
  const activePanel = ref(null);
  const isActivePanelInside = ref(false);
  const isHoveredPanelInside = ref(false);
  const haveCanvasesBeenModified = ref(true);
  const configuration = ref({
    meta: {},
    box: {},
    canvases: []
  });
  const boxQuantity = ref(100);
  const flapHeight = ref(null);
  const flapWidth = ref(null);
  const flapGap = ref(null);
  const overlapWidth = ref(null);
  const activeObjects = ref([]);
  const bleed = ref(BLEED);
  const baseObjectSize = ref(null);
  const hasInsidePrinting = ref(false);
  const insideMaterialColor = ref(COLOR_KRAFT);
  const outsideMaterialColor = ref(COLOR_WHITE);
  const materialThickness = ref(MATERIAL_THICKNESS);

  // COMPUTED ----------------------------------------------------------------
  // when we have multiple canvases set active object for each canvas to null before reding and setting from here
  // that will insure that more than one canvas can't have active objects at the same time
  // const activeObjects = computed(() => configuration.value.activeObjects);

  const isOutsideCanvasActive = computed(() => activeCanvas.value === fabricCanvases.value[0]);
  const configurationId = computed(() => configuration.value._id);
  const variationId = computed(() => configuration.value.meta.variationId);
  const variationSlug = computed(() => configuration.value.meta.slug);
  const screenshotUrl = computed(() => configuration.value.meta.screenshotUrl);
  const boxType = computed(() => configuration.value.box.boxType);
  const boxWidth = computed(() => configuration.value.box.boxWidth);
  const boxLength = computed(() => configuration.value.box.boxLength);
  const boxDepth = computed(() => configuration.value.box.boxDepth);
  const canvases = computed(() => configuration.value.canvases);
  const boxWidthInPx = computed(() => boxWidth.value * pixelsPerInch.value);
  const boxLengthInPx = computed(() => boxLength.value * pixelsPerInch.value);
  const boxDepthInPx = computed(() => boxDepth.value * pixelsPerInch.value);
  const materialThicknessInPx = computed(() => materialThickness.value * pixelsPerInch.value);
  const overlapWidthInPx = computed(() => overlapWidth.value * pixelsPerInch.value);
  const bleedInPx = computed(() => bleed.value * pixelsPerInch.value);
  const flapHeightInPx = computed(() => flapHeight.value * pixelsPerInch.value);
  const workspaces = computed(() => canvases.value.workspaces);
  const offscreenCanvases = computed(() => canvases.value.offscreenCanvases);
  const activeCanvasBackgroundColor = computed(() => activeCanvas.value.backgroundColor);

  // HELPERS ----------------------------------------------------------------
  function configureShippingBox(configuration) {
    materialThickness.value = MATERIAL_THICKNESS_SHIPPING;
    flapGap.value = materialThickness.value;
    flapHeight.value = configuration.value.box.boxLength / 2;
  }
  function configureMailerBox(configuration) {
    materialThickness.value = MATERIAL_THICKNESS;
    const maximumFlapWidth = configuration.value.box.boxDepth - materialThickness.value * 3;
    let _flapWidth = configuration.value.box.boxLength / 2 + materialThickness.value * 3;
    if (_flapWidth > maximumFlapWidth) {
      _flapWidth = maximumFlapWidth;
    }
    flapWidth.value = _flapWidth;
    overlapWidth.value = materialThickness.value * 5 || overlapWidth.value;
  }
  function configureLiteratureBox(configuration) {
    materialThickness.value = MATERIAL_THICKNESS;
    const maximumFlapWidth = configuration.value.box.boxDepth - materialThickness.value * 3;
    let _flapWidth = configuration.value.box.boxLength / 2 + materialThickness.value * 3;
    if (_flapWidth > maximumFlapWidth) {
      _flapWidth = maximumFlapWidth;
    }
    flapWidth.value = _flapWidth;
    overlapWidth.value = materialThickness.value * 5 || overlapWidth.value;
  }
  function setMaterialColorsFromSlug(slug) {
    const materialMap = {
      w: COLOR_WHITE,
      k: COLOR_KRAFT
    };
    const materialCode = slug.split('-')[4] || 'kk';
    const outsideMaterial = materialMap[materialCode[0]];
    const insideMaterial = materialMap[materialCode[1]];
    setInsideMaterialColor(insideMaterial);
    setOutsideMaterialColor(outsideMaterial);
  }
  function getColorByMaterial(material) {
    const colors = {
      kraft: COLOR_KRAFT,
      white: COLOR_WHITE
    };
    return colors[material] || colors.outside;
  }

  // ACTIONS ----------------------------------------------------------------
  /**
   * Set configuration
   * @param {Object} configurationObject
   *  @param {Number|String} configurationObject.meta.variationId
   *  @param {String} configurationObject.meta.slug
   *  @param {String} configurationObject.meta.screenshotUrl
   *  @param {String} configurationObject.meta.hash
   *  @param {String} configurationObject.box.boxType
   *  @param {Number} configurationObject.box.boxQuantity
   *  @param {Number} configurationObject.box.boxWidth
   *  @param {Number} configurationObject.box.boxLength
   *  @param {Number} configurationObject.box.boxDepth
   *  @param {String} configurationObject._id
   *  @param {Array} configurationObject.canvases
   * @returns {void}
   */
  function setConfiguration(configurationObject) {
    configuration.value = Object.assign({}, configurationObject);
    const boxType = configuration.value.box.boxType;
    if (boxType === 'Shipping') {
      configureShippingBox(configuration);
    }

    // @TODO: Mailer and Literature should be refactored to use the same logic
    if (boxType === 'Mailer') {
      configureMailerBox(configuration);
    }
    if (boxType === 'Literature') {
      configureLiteratureBox(configuration);
    }
  }
  function clearConfiguration() {
    configuration.value = {
      meta: {},
      box: {},
      canvases: []
    };
    boxQuantity.value = 25;
    flapHeight.value = null;
    flapWidth.value = null;
    flapGap.value = null;
    overlapWidth.value = null;
    activeObjects.value = [];
    bleed.value = BLEED;
    baseObjectSize.value = null;
    hasInsidePrinting.value = false;
    insideMaterialColor.value = COLOR_KRAFT;
    outsideMaterialColor.value = COLOR_WHITE;
    materialThickness.value = MATERIAL_THICKNESS;
    fabricCanvases.value = [];
  }
  function getCanvasPrintSideIndex(printSide) {
    const sides = {
      outside: 0,
      inside: 1
    };
    return sides[printSide];
  }
  function setConfigurationScreenShotData(payload) {
    configurationScreenshotData.value = payload;
  }
  function setHoveredPanel(payload) {
    hoveredPanel.value = payload;
  }
  function setActivePanel(payload = null) {
    activePanel.value = payload;
  }
  function setActiveObjects(payload) {
    activeObjects.value = payload;
  }

  // @NOTE: Not in use
  function setCanvases(payload) {
    configuration.value.canvases.workspaces = payload.workspaces;
    configuration.value.canvases.offscreenCanvases = payload.offscreenCanvases;
  }
  function setBaseObjectSize(payload) {
    baseObjectSize.value = payload;
  }
  function addOffscreenCanvasId(payload) {
    const canvasIndex = getCanvasPrintSideIndex(payload.printSide);
    offscreenCanvasIds.value[canvasIndex] = payload.offscreenCanvasId;
  }
  function addFabricCanvas(payload) {
    const canvasIndex = getCanvasPrintSideIndex(payload.printSide);
    fabricCanvases.value[canvasIndex] = payload.fabricInstance;

    /**
     * @NOTE:
     *  When hot-reloading new canvases are created so must set active canvas to null
     */
    if (activeCanvas.value && !activeCanvas.value.lowerCanvasEl) {
      activeCanvas.value = null;
    }

    // @TEMP way to set active canvas to outside canvas
    if (activeCanvas.value == null) {
      setActiveCanvas(fabricCanvases.value[canvasIndex]);
    }
  }
  function setIsActivePanelInside(payload) {
    isActivePanelInside.value = payload;
    // TEMP set here
    const canvas = payload ? fabricCanvases.value[1] : fabricCanvases.value[0];
    setActiveCanvas(canvas);
  }
  function setIsHoveredPanelInside(payload) {
    isHoveredPanelInside.value = payload;
  }
  function setActiveCanvas(payload) {
    // @NOTE: Set activePanel - TEMP
    setActivePanel();
    activeCanvas.value = payload;
  }
  function setBox(payload) {
    box.value = payload;
  }
  function setBoxQuantity(quantity) {
    boxQuantity.value = quantity;
  }
  function setHasInsidePrinting(payload) {
    hasInsidePrinting.value = payload;
  }
  function setInsideMaterialColor(material) {
    insideMaterialColor.value = getColorByMaterial(material);
  }
  function setOutsideMaterialColor(material) {
    outsideMaterialColor.value = getColorByMaterial(material);
  }
  function discardActiveObjects() {
    if (!fabricCanvases.value.length) return;
    fabricCanvases.value.forEach(c => {
      c.discardActiveObject().renderAll();
    });
  }
  function setProduct(payload) {
    product.value = payload;
  }
  function objectModified() {
    activeCanvas.value.fire('object:modified');
  }
  function setHaveCanvasesBeenModified(payload) {
    haveCanvasesBeenModified.value = payload;
  }
  return {
    product,
    configuration,
    variationId,
    variationSlug,
    screenshotUrl,
    boxQuantity,
    configurationId,
    canvases,
    activeObjects,
    hoveredPanel,
    activePanel,
    boxType,
    boxWidth,
    boxLength,
    boxDepth,
    overlapWidth,
    materialThickness,
    flapHeight,
    bleed,
    boxWidthInPx,
    boxLengthInPx,
    boxDepthInPx,
    materialThicknessInPx,
    overlapWidthInPx,
    bleedInPx,
    flapHeightInPx,
    workspaces,
    offscreenCanvases,
    baseObjectSize,
    offscreenCanvasIds,
    fabricCanvases,
    isActivePanelInside,
    activeCanvas,
    box,
    hasInsidePrinting,
    isHoveredPanelInside,
    insideMaterialColor,
    outsideMaterialColor,
    pixelsPerInch,
    activeCanvasBackgroundColor,
    isOutsideCanvasActive,
    configurationScreenshotData,
    haveCanvasesBeenModified,
    setHaveCanvasesBeenModified,
    setConfiguration,
    setHoveredPanel,
    setActivePanel,
    setConfigurationScreenShotData,
    setActiveObjects,
    setCanvases,
    setBaseObjectSize,
    addOffscreenCanvasId,
    addFabricCanvas,
    setIsActivePanelInside,
    setActiveCanvas,
    setBox,
    setBoxQuantity,
    setHasInsidePrinting,
    setIsHoveredPanelInside,
    setMaterialColorsFromSlug,
    setInsideMaterialColor,
    setOutsideMaterialColor,
    discardActiveObjects,
    clearConfiguration,
    getCanvasPrintSideIndex,
    setProduct,
    objectModified
  };
});